<template>
  <l-dropdown
    v-model:open="showDropdown"
    :options="dropdownOptions"
    :mark-selected="false"
    placement="bottom-end"
  >
    <l-button class="relative" size="small" icon="3-dots" @click="showDropdown = !showDropdown" />
  </l-dropdown>
  <move-products-between-tabs
    v-if="selected === 'move-products'"
    :from-tab-id="tabId"
    class="absolute left-0 top-0 z-20"
    @products-moved="goToTab"
    @close="selected = undefined"
  />
  <kitchen-orders
    v-if="selected === 'kitchen-orders'"
    :tab-id="tabId"
    @close="selected = undefined"
  />
  <send-kitchen-note
    v-if="selected === 'kitchen-note'"
    :tab-id="tabId"
    @close="selected = undefined"
  />
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LButton, LDropdown } from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/dropdown/types'

import { useTabs } from '@/composables/useTabs'
import { Beeper as BeeperIntegration } from '@/integrations/beeper/beeper'
import { useBeepersStore } from '@/store/beepers'
import { useConfigStore } from '@/store/config'
import { useFloorplanStore } from '@/store/floorplan'

import MoveProductsBetweenTabs from './MoveProductsBetweenTabs.vue'
import KitchenOrders from './KitchenOrders.vue'
import SendKitchenNote from './SendKitchenNote.vue'

const props = defineProps<{
  tabId: string
}>()

const { t } = useI18n()
const router = useRouter()

const { beeper } = useTabs(() => props.tabId)

const configStore = useConfigStore()
const beeperStore = useBeepersStore()
const floorplanStore = useFloorplanStore()

const { config } = storeToRefs(configStore)
const { beepers } = storeToRefs(beeperStore)
const { floorplanIds } = storeToRefs(floorplanStore)

const showDropdown = ref(false)
const selected = ref<string>()

const dropdownOptions = computed(() => {
  const options: Option[] = [
    {
      icon: 'kitchen-note',
      label: t('ordering.kitchen-note'),
      value: 'kitchen-note',
      onClick: () => {
        selected.value = 'kitchen-note'
      }
    }
  ]

  if (floorplanIds.value.length > 0) {
    options.push({
      icon: 'double-arrow-inside',
      label: t('ordering.move-products'),
      value: 'move-products',
      onClick: () => {
        selected.value = 'move-products'
      }
    })
  }

  if (config.value.enableKitchenOrders) {
    options.push({
      icon: 'refresh',
      label: t('kitchen-orders.title'),
      value: 'kitchen-orders',
      onClick: () => {
        selected.value = 'kitchen-orders'
      }
    })
  }

  if (Object.keys(beepers.value).length > 0) {
    options.push({
      icon: 'bell',
      label: t('beepers.assign-beeper'),
      value: 'assign-beeper',
      onClick: () => {
        router.push({ name: 'beepers', query: { tabId: props.tabId } })
      }
    })

    if (beeper.value) {
      options.push({
        icon: 'lightning',
        label: t('beepers.activate-beeper'),
        value: 'activate-beeper',
        onClick: () => {
          BeeperIntegration.getInstance().beep('touchIt', beeper.value?.id as string)
        }
      })
    }
  }
  return options
})

function goToTab(tabId: string) {
  router.push({ name: 'orderManagement', params: { tabId } })
}
</script>
